import {Injectable, isDevMode} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/shareReplay';
import 'rxjs/add/observable/throw';
import {ProgressService} from './progress.service';
import {catchError, tap} from 'rxjs/operators';
import {of} from 'rxjs/observable/of';
import {SnackService} from './snack.service';
import {BaseObject} from '../model/Entities/BaseObject';
import {SortInfo} from '../model/Helpers/SortInfo';
import {Filter} from '../model/Helpers/Filter';
import {Pagination} from '../model/Helpers/Pagination';
import {Tools} from '../model/Helpers/Tools';
import {LocalStorageService} from './local-storage.service';
import {ProductContent} from '../model/Entities/ProductContent';
import {BaseService} from './base.service';
import {Router} from '@angular/router';

@Injectable()
export abstract class ApiService<T extends BaseObject> extends BaseService {
    private static RECOVERABLE_ERRORS = [400, 401];

    protected apiUrl;  // URL to web api

    protected httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json'
        }),
        params: new HttpParams()
    };

    /** Log a HeroService message with the MessageService */
    private static log(message: string) {
        // console.log('LOG SERVICE: ', message);
        // this.messageService.add('HeroService: ' + message);
    }

    protected abstract getObjectType(): any;

    constructor(private http: HttpClient,
                protected progressService: ProgressService,
                protected snackService: SnackService,
                protected localStorageService: LocalStorageService,
                protected router: Router) {
        super();
    }

    initAuthorizationHeader(): void {
        if (this.localStorageService.has('user-token')) {
            this.httpOptions.headers
                = this.httpOptions.headers.set('Authorization', 'Bearer ' + this.localStorageService.get('user-token'));
        }
    }

    getSimpleList(filters: Filter[] = [], params: Object = {}): Observable<T[]> {
        return this.getList(filters, params)
            .map((data) => {
                return this.mapDataCollectionToObjectCollection(data);
            })
            .map(this.simpleListResponseToCamelCase);
    }

    getPaginatedList(pagination: Pagination = null, sort: SortInfo = null, filters: Filter[] = [], params: Object = {}): Observable<any> {

        if (pagination !== null) {
            params['limit'] = pagination.limit;
            params['offset'] = pagination.offset;
        }

        if (sort !== null && sort.dir === SortInfo.DIR_DESC) {
            params['order'] = '-' + sort.col;
        } else if (sort !== null) {
            params['order'] = sort.col;
        }

        return this.getList(filters, params)
            .map((data) => {
                data.results = this.mapDataCollectionToObjectCollection(data.results);
                return data;
            });
    }

    get(id: Number, params: Object = {}): Observable<T> {
        return this.sendGet(`${this.apiRoot}/${this.apiUrl}/${id}`, params)
            .map((data) => {
                return this.mapDataToObject(data);
            })

            /** TRANSFORM property names from camel_case to cameCase **/
            .map((data) => this.objectResponseToCamelCase(data));
    }

    save(data: any, params: object = {}): Observable<any> {
        data = this.decamelizeObject(data);
        return this.sendPost(`${this.apiRoot}/${this.apiUrl}`, data)
            .map((resultData) => {
                return this.mapDataToObject(resultData);
            })

            /** TRANSFORM property names from camel_case to cameCase **/
            .map((resultData) => this.objectResponseToCamelCase(resultData));
    }

    update(id: Number, data: any): Observable<any> {
        data = this.decamelizeObject(data);
        return this.sendPut(`${this.apiRoot}/${this.apiUrl}/${id}`, data);
    }

    delete(id: Number): Observable<any> {
        return this.sendDelete(`${this.apiRoot}/${this.apiUrl}/${id}`);
    }

    clone(id: number, data: object = {}): Observable<T> {
        data = this.decamelizeObject(data);
        return this.sendPost(`${this.apiRoot}/${this.apiUrl}?clone=${id}`, data)
            .map((resultData) => {
                return this.mapDataToObject(resultData);
            })

            /** TRANSFORM property names from camel_case to cameCase **/
            .map((resultData) => this.objectResponseToCamelCase(resultData));
    }

    private getList(filters: Filter[] = [], params: Object = {}): Observable<any> {
        return this.sendGet(`${this.apiRoot}/${this.apiUrl}`, params, filters)

        /** TRANSFORM property names from camel_case to cameCase **/
            .map(data => {
                return this.listResponseToCamelCase(data);
            });
        // return this.http
        //     .get<any>(`${this.apiRoot}/${this.apiUrl}`, this.httpOptions);
    }

    private mapDataToObject(data: any) {
        const obj = Object.assign(new (this.getObjectType())(), data);


        return obj;
    }

    private mapDataCollectionToObjectCollection(data: any[]) {
        if (!data) {
            console.error('mapDataCollectionToObjectCollection', 'The data is not collection');
            return data;
        }

        for (let i = 0; i < data.length; i++) {
            data[i] = this.mapDataToObject(data[i]);
        }
        return data;
    }

    private setHttpParams(params: object = {}) {
        this.httpOptions.params = new HttpParams();
        for (const paramIndex in params) {
            if (params.hasOwnProperty(paramIndex)) {
                this.httpOptions.params = this.httpOptions.params.set(paramIndex, String(params[paramIndex]));
            }
        }
        return this.httpOptions;
    }

    private simpleListResponseToCamelCase(data) {
        if (!data.length || data.length <= 0) {
            return data;
        }

        for (let i = 0; i < data.length; i++) {
            const d = data[i];
            for (const key in d) {
                if (d.hasOwnProperty(key)) {
                    data[i][Tools.camelize(key)] = d[key];
                }
            }
        }

        return data;
    }

    private listResponseToCamelCase(data) {
        if (!data.results) {
            return data;
        }

        data.results = this.simpleListResponseToCamelCase(data.results);

        return data;
    }

    protected objectResponseToCamelCase(data) {
        for (const key in data) {
            const ccKey = Tools.camelize(key);
            if (typeof data[key] === 'object') {
                data[key] = this.objectResponseToCamelCase(data[key]);
            } else if (key === ccKey) {
                continue;
            } else if (data.hasOwnProperty(key) && Array.isArray(data[key])) {
                data[ccKey] = [];
                data[key].forEach((item) => {
                    data[ccKey].push(item);
                });
            } else if (data.hasOwnProperty(key)) {
                data[ccKey] = data[key];
            }
        }

        return data;
    }

    protected decamelizeObject(data) {
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                data[Tools.decamelize(key)] = data[key];
            }
        }

        return data;
    }

    protected sendGet(url: string, params: object = {}, filters: Filter[] = []): Observable<any> {

        url += this.getFiltersUrlQuery(filters);

        // after the url is ready
        this.progressService.startLoading(`API-GET-${url}`);

        // console.log('GET', url);

        this.initAuthorizationHeader();
        this.setHttpParams(params);

        return this.http
            .get<T>(url, this.httpOptions)
            .pipe(
                tap(() => ApiService.log(`sent GET ${url}`)),
                catchError(this.handleError(url, 'GET'))
            )
            .do(() => {
                this.progressService.stopLoading(`API-GET-${url}`);
            });
    }

    protected sendPost(url: string, data: any, params: object = {}): Observable<any> {
        this.progressService.startLoading(`API-POST-${url}`);

        this.initAuthorizationHeader();
        this.setHttpParams(params);

        return this.http
            .post<any>(url, data, this.httpOptions)
            .pipe(
                tap(() => ApiService.log(`sent POST ${url}`)),
                catchError(this.handleError(url, 'POST'))
            )
            .do(() => {
                this.progressService.stopLoading(`API-POST-${url}`);
            });
    }

    protected sendPut(url: string, data: any, params: object = {}): Observable<any> {
        this.progressService.startLoading(`API-PUT-${url}`);

        this.initAuthorizationHeader();
        this.setHttpParams(params);

        return this.http
            .put<any>(url, data, this.httpOptions)
            .pipe(
                tap(() => ApiService.log(`sent PUT ${url}`)),
                catchError(this.handleError(url, 'PUT'))
            )
            .do(() => {
                this.progressService.stopLoading(`API-PUT-${url}`);
            });
    }

    protected sendDelete(url: string, params: object = {}): Observable<any> {
        this.progressService.startLoading(`API-DELETE-${url}`);

        this.initAuthorizationHeader();
        this.setHttpParams(params);

        return this.http
            .delete<any>(url, this.httpOptions)
            .pipe(
                tap(() => ApiService.log(`sent DELETE ${url}`)),
                catchError(this.handleError(url, 'DELETE'))
            )
            .do(() => {
                this.progressService.stopLoading(`API-DELETE-${url}`);
            });
    }

    // private handleError(error: any): Promise<any> {
    //     console.error('An error occurred', error); // for demo purposes only
    //     return Promise.reject(error.message || error);
    // }

    /**
     * Handle Http operation that failed.
     * Let the app continue.
     * @param operation - name of the operation that failed
     * @param result - optional value to return as the observable result
     */
    private handleError(operation = 'operation', method, result?: T) {
        return (error: any): Observable<T> => {

            this.progressService.stopLoading(`API-${method}-${operation}`);

            if (error.status === 401 && error.statusText === 'Unauthorized') {
                this.router.navigate(['login']);
                this.localStorageService.unset('logged-user');
                this.localStorageService.unset('user-token');
                this.snackService.showWarn('Nejprve se přihlašte');

                return of(result as T);
            }

            if (ApiService.RECOVERABLE_ERRORS.indexOf(error.status) >= 0) {
                return Observable.throw(error);
            }

            if (error.status === 403) {
                this.snackService.showWarn('ERROR 403 following...');
                return Observable.throw(error);
            }

            // TODO: send the error to remote logging infrastructure
            console.log(method, 'HTTP Error following...');
            console.error(error); // log to console instead

            // TODO: better job of transforming error for user consumption
            ApiService.log(`${operation} failed: ${error.message}`);

            this.snackService.showWarn('Objevila se chyba při komunikaci se serverem');

            return Observable.throw(error);

            // Let the app keep running by returning an empty result.
            // return of(result as T);
        };
    }
}



// WEBPACK FOOTER //
// /app/src/services/api.service.ts